import Decimal from 'decimal.js';
import { decodePlusCode, findPlusCode } from './plus-code';

/**
 * P06 B6 – phí vận chuyển + cẩu (docs/phases/P06.md §5 mục 8). Hàm thuần, dùng chung API (nguồn sự thật) và giao diện.
 *
 *   km          = km đi (1 chiều) hoặc km đi + km về (2 chiều, NV nhập từng chiều, KHÔNG tự nhân 2)   [DEC-105]
 *   tiền xe     = round(km × đơn giá/km của xe đang hiệu lực)                                        [DEC-046, DEC-102]
 *   số ca cẩu   = làm tròn LÊN bội số 0,5 (nửa ca hoặc nguyên ca), tối thiểu 0,5; 0 = không cẩu       [DEC-104]
 *   tiền cẩu    = round(số ca × đơn giá ca cẩu của xe)                                              [DEC-046: 3.500.000 đ/ca 8 giờ]
 *   phí         = tiền xe + tiền cẩu → 1 dòng dịch vụ "Vận chuyển" trên báo giá                       [DEC-135]
 * Không có ngưỡng miễn phí theo tấn (DEC-135). Chọn xe theo tải tối đa của xe (DEC-046: ≤ 1,9 t, ≤ 3,4 t, trên đó xe cầu,
 * xe cầu mặc định 61H006.09 – DEC-103); mốc tải là dữ liệu trên màn Xe, không ghi cứng ở đây.
 * Tiền VND làm tròn ROUND_HALF_UP 0 số lẻ như quote-calc.
 */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

export const SHIPPING_LINE_NAME = 'Vận chuyển';
export const KM_DP = 1;
export const CRANE_SHIFT_STEP = 0.5;
export const TRIP_MODES = ['one_way', 'round_trip'] as const;
export type TripMode = (typeof TRIP_MODES)[number];
export const TRIP_MODE_LABELS: Record<TripMode, string> = { one_way: '1 chiều', round_trip: '2 chiều' };

export interface ShippingWeightLine {
  quantity: number | string;
  /** kg / 1 ĐVT chính (từ B3) */
  weightKg?: number | string | null;
  /** SL quy về ĐVT chính; không có = SL */
  baseQuantity?: number | string | null;
  /** Dòng dịch vụ (vận chuyển…) không tính vào tải */
  isService?: boolean;
}

/**
 * Tổng tải W (kg) của TẤT CẢ dòng hàng = Σ kg/ĐVT chính × SL ĐVT chính. Dòng hàng thiếu khối lượng (null/0) không cộng,
 * trả vị trí từng dòng (`missingIndexes`, theo thứ tự mảng đầu vào) để liệt kê cho NV.
 */
export function shippingWeightKg(lines: ShippingWeightLine[]): { weightKg: number; linesMissingWeight: number; missingIndexes: number[] } {
  let w = new D(0);
  const missingIndexes: number[] = [];
  for (const [i, l] of lines.entries()) {
    if (l.isService) continue;
    const kg = l.weightKg === null || l.weightKg === undefined || l.weightKg === '' ? null : new D(l.weightKg);
    if (!kg || kg.lte(0)) {
      missingIndexes.push(i);
      continue;
    }
    const qty = new D(l.baseQuantity ?? l.quantity);
    w = w.plus(kg.times(qty));
  }
  return { weightKg: w.toDecimalPlaces(3).toNumber(), linesMissingWeight: missingIndexes.length, missingIndexes };
}

/** Số ca cẩu tính tiền: làm tròn lên bội số 0,5, tối thiểu 0,5 ca; 0 (hoặc bỏ trống) = không cẩu. VD 1,2 → 1,5; 1,6 → 2. */
export function roundCraneShifts(raw: number | null | undefined): number {
  if (raw === null || raw === undefined || !(raw > 0)) return 0;
  const steps = new D(raw).div(CRANE_SHIFT_STEP).toDecimalPlaces(6).ceil();
  return Decimal.max(steps, 1).times(CRANE_SHIFT_STEP).toNumber();
}

/** Tổng km tính tiền. 2 chiều = km đi + km về (NV nhập từng chiều), không tự nhân 2. */
export function tripKm(mode: TripMode, kmOut: number, kmBack?: number | null): number {
  if (!(kmOut > 0)) throw new RangeError('Nhập số km chiều đi lớn hơn 0');
  if (mode === 'one_way') return new D(kmOut).toDecimalPlaces(KM_DP).toNumber();
  if (kmBack === null || kmBack === undefined || !(kmBack > 0)) throw new RangeError('Chọn 2 chiều thì nhập số km chiều về lớn hơn 0');
  return new D(kmOut).toDecimalPlaces(KM_DP).plus(new D(kmBack).toDecimalPlaces(KM_DP)).toNumber();
}

export interface ShippingVehicleLite {
  id: number;
  maxLoadKg: number | null;
  isCrane: boolean;
  isDefault: boolean;
  isActive: boolean;
}

/**
 * Xe đề xuất theo tải (DEC-046): xe thường đang dùng nhỏ nhất có tải tối đa ≥ W; không xe thường nào chở được thì xe cầu
 * mặc định (DEC-103), không có thì xe cầu đang dùng đầu tiên. NV vẫn được chọn xe khác.
 */
export function suggestVehicle<T extends ShippingVehicleLite>(weightKg: number, vehicles: T[]): T | null {
  const active = vehicles.filter((v) => v.isActive);
  const fit = active
    .filter((v) => !v.isCrane && v.maxLoadKg !== null && v.maxLoadKg >= weightKg)
    .sort((a, b) => a.maxLoadKg! - b.maxLoadKg! || a.id - b.id);
  if (fit[0]) return fit[0];
  // Vượt mọi xe thường → xe cầu (DEC-046); xe cầu có ghi tải tối đa thì cũng phải đủ tải. Không xe nào đủ → null.
  const cranes = active.filter((v) => v.isCrane && (v.maxLoadKg === null || v.maxLoadKg >= weightKg)).sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || a.id - b.id);
  return cranes[0] ?? null;
}

export interface ShippingCalcInput {
  tripMode: TripMode;
  kmOut: number;
  kmBack?: number | null;
  /** đ/km của xe đang hiệu lực ngày lập */
  ratePerKm: number;
  isCrane: boolean;
  /** đ/ca cẩu của xe; xe thường = null */
  craneRatePerShift?: number | null;
  /** Số ca NV nhập (chưa làm tròn); 0/null = không cẩu */
  craneShifts?: number | null;
}

export interface ShippingCalc {
  km: number;
  transportFee: number;
  /** Số ca đã làm tròn (bội 0,5) */
  craneShifts: number;
  craneFee: number;
  /** Tiền 1 dòng "Vận chuyển" = tiền xe + tiền cẩu (DEC-135) */
  total: number;
}

export function calcShipping(i: ShippingCalcInput): ShippingCalc {
  if (!(i.ratePerKm >= 0)) throw new RangeError('Xe chưa có đơn giá/km');
  const km = tripKm(i.tripMode, i.kmOut, i.kmBack);
  const transportFee = new D(km).times(i.ratePerKm).toDecimalPlaces(0);
  const shifts = roundCraneShifts(i.craneShifts);
  if (shifts > 0 && !i.isCrane) throw new RangeError('Xe đã chọn không phải xe cầu, không tính ca cẩu');
  if (shifts > 0 && (i.craneRatePerShift === null || i.craneRatePerShift === undefined))
    throw new RangeError('Xe cầu chưa có đơn giá ca cẩu');
  const craneFee = shifts > 0 ? new D(shifts).times(i.craneRatePerShift!).toDecimalPlaces(0) : new D(0);
  return {
    km,
    transportFee: transportFee.toNumber(),
    craneShifts: shifts,
    craneFee: craneFee.toNumber(),
    total: transportFee.plus(craneFee).toNumber(),
  };
}

/**
 * Chi tiết phí lưu trên dòng "Vận chuyển" (quote_items.shipping, DEC-135): đủ để duyệt và đối soát, không in ra báo giá.
 * `amount` = tiền xe + tiền cẩu đã tính; tiền sau CK của dòng khác `amount` thì phải có `overrideReason` (§5 mục 8).
 */
export interface ShippingSnapshot {
  vehicleId: number;
  plateNo: string;
  isCrane: boolean;
  serviceCode: string;
  /** Ngày lập báo giá dùng để lấy đơn giá */
  date: string;
  rateEffectiveFrom: string;
  ratePerKm: number;
  craneRatePerShift: number | null;
  weightKg: number;
  linesMissingWeight: number;
  tripMode: TripMode;
  kmOut: number;
  kmBack: number | null;
  km: number;
  craneShiftsInput: number | null;
  craneShifts: number;
  transportFee: number;
  craneFee: number;
  amount: number;
  overrideReason?: string | null;
  /** Địa chỉ / link định vị NV dùng để đo km (DEC-105, bổ sung 06/10/2026) */
  destination?: string | null;
  /** Km 1 chiều bản đồ tính được từ kho tới điểm giao; null = NV nhập tay */
  routeKm?: number | null;
}

// ---------------------------------------------------------------------------
// Điểm giao: địa chỉ chữ, tọa độ hoặc link Google Maps (khách gửi định vị qua Zalo)
// ---------------------------------------------------------------------------
export type ShippingLocation =
  | { kind: 'latlng'; lat: number; lng: number }
  | { kind: 'shortlink'; url: string }
  | { kind: 'address'; text: string };

const SHORTLINK_HOSTS = ['maps.app.goo.gl', 'goo.gl'];
const MAPS_HOSTS = /^(www\.|maps\.)?google\.[a-z.]+$/;
const NUM = String.raw`-?\d{1,3}(?:\.\d+)?`;

function latLng(lat: string, lng: string): ShippingLocation | null {
  const a = Number(lat);
  const b = Number(lng);
  return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a) <= 90 && Math.abs(b) <= 180 ? { kind: 'latlng', lat: a, lng: b } : null;
}

/** Tọa độ trong link Google Maps dạng đầy đủ (…/@10.84,106.75,17z, ?q=10.84,106.75, !3d10.84!4d106.75); không có thì null. */
export function latLngFromMapsUrl(url: URL): ShippingLocation | null {
  const pin = url.pathname.match(new RegExp(`!3d(${NUM})!4d(${NUM})`)) ?? url.href.match(new RegExp(`!3d(${NUM})!4d(${NUM})`));
  if (pin) return latLng(pin[1]!, pin[2]!);
  for (const k of ['q', 'query', 'destination', 'll', 'daddr']) {
    const m = url.searchParams.get(k)?.match(new RegExp(`^\\s*(${NUM})\\s*,\\s*(${NUM})\\s*$`));
    if (m) return latLng(m[1]!, m[2]!);
  }
  const at = url.pathname.match(new RegExp(`/@(${NUM}),(${NUM})`));
  return at ? latLng(at[1]!, at[2]!) : null;
}

/**
 * Đọc ô "Điểm giao": "10.8456, 106.7512" → tọa độ; link maps.app.goo.gl → server mở link lấy tọa độ;
 * link google.com/maps có tọa độ → tọa độ; link có ?q=<địa chỉ> → địa chỉ; địa chỉ có Plus Code đầy đủ (7P386QR9+WFW) → tọa độ;
 * còn lại là địa chỉ chữ (Plus Code rút gọn "6QR9+WFW" cần điểm tham chiếu, server giải ở locatePoint).
 */
export function parseShippingLocation(input: string): ShippingLocation | null {
  const text = input.trim().replace(/\s+/g, ' ');
  if (!text) return null;
  const pair = text.match(new RegExp(`^(${NUM})\\s*,\\s*(${NUM})$`));
  if (pair) return latLng(pair[1]!, pair[2]!) ?? { kind: 'address', text };
  const link = text.match(/https?:\/\/\S+/)?.[0];
  if (link) {
    let url: URL;
    try {
      url = new URL(link);
    } catch {
      return { kind: 'address', text };
    }
    const host = url.hostname.toLowerCase();
    if (url.protocol === 'https:' && SHORTLINK_HOSTS.includes(host)) return { kind: 'shortlink', url: url.href };
    if (MAPS_HOSTS.test(host)) {
      const ll = latLngFromMapsUrl(url);
      if (ll) return ll;
      const q = url.searchParams.get('q') ?? url.searchParams.get('query') ?? url.searchParams.get('destination');
      if (q?.trim()) return { kind: 'address', text: q.trim() };
      const place = url.pathname.match(/\/place\/([^/]+)/)?.[1];
      if (place) return { kind: 'address', text: decodeURIComponent(place.replace(/\+/g, ' ')) };
    }
    return { kind: 'address', text };
  }
  const pc = findPlusCode(text);
  const full = pc?.full ? decodePlusCode(pc.code) : null;
  if (full) return { kind: 'latlng', lat: full.lat, lng: full.lng };
  return { kind: 'address', text };
}

/** Link mở chỉ đường Google Maps từ kho tới điểm giao (NV xem lại tuyến, đo tay khi cần). */
export function mapsDirectionsUrl(origin: string, destination: string): string {
  const loc = parseShippingLocation(destination);
  const dest = loc?.kind === 'latlng' ? `${loc.lat},${loc.lng}` : destination.trim();
  const p = new URLSearchParams({ api: '1', travelmode: 'driving', destination: dest });
  if (origin.trim()) p.set('origin', origin.trim());
  return `https://www.google.com/maps/dir/?${p.toString()}`;
}
